// eslint-disable-next-line no-restricted-imports
import { Tooltip, TooltipProps, makeStyles } from '@material-ui/core';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

export enum Severity {
  Default,
  Info,
  Warning,
  Error,
}

const useStyles = makeStyles(theme => ({
  infoTooltip: {
    backgroundColor: theme.palette.bkg.info,
    border: `1px solid ${theme.palette.info.dark}`,
    borderRadius: 4,
    color: theme.palette.black,
    '& p': {
      color: theme.palette.black,
    },
  },
  infoTooltipArrow: {
    color: theme.palette.bkg.info,
    '&::before': {
      border: `1px solid ${theme.palette.info.dark}`,
      boxSizing: 'border-box',
    },
  },
  warningTooltip: {
    backgroundColor: theme.palette.bkg.warning,
    border: `1px solid ${theme.palette.warning.dark}`,
    borderRadius: 4,
    color: theme.palette.black,
    '& p': {
      color: theme.palette.black,
    },
  },
  warningTooltipArrow: {
    color: theme.palette.bkg.warning,
    '&::before': {
      border: `1px solid ${theme.palette.warning.dark}`,
      boxSizing: 'border-box',
    },
  },
  errorTooltip: {
    backgroundColor: theme.palette.bkg.error,
    border: `1px solid ${theme.palette.error.dark}`,
    borderRadius: 4,
    color: theme.palette.black,
    '& p': {
      color: theme.palette.black,
    },
  },
  errorTooltipArrow: {
    color: theme.palette.bkg.error,
    '&::before': {
      border: `1px solid ${theme.palette.error.dark}`,
      boxSizing: 'border-box',
    },
  },
}));

export interface WbTooltipProps extends Omit<TooltipProps, 'open'> {
  open?: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
  severity?: Severity;
}

export const WbTooltip = (props: WbTooltipProps) => {
  const {
    title,
    children,
    open: openProp,
    setOpen: setOpenProp,
    severity,
    ...rest
  } = props;

  const classes = useStyles();
  let tooltipClasses: TooltipProps['classes'];
  switch (severity) {
    case Severity.Info:
      tooltipClasses = {
        tooltip: classes.infoTooltip,
        arrow: classes.infoTooltipArrow,
      };
      break;
    case Severity.Warning:
      tooltipClasses = {
        tooltip: classes.warningTooltip,
        arrow: classes.warningTooltipArrow,
      };
      break;
    case Severity.Error:
      tooltipClasses = {
        tooltip: classes.errorTooltip,
        arrow: classes.errorTooltipArrow,
      };
      break;
    default:
      break;
  }

  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = openProp !== undefined && setOpenProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = isControlled ? setOpenProp : setInternalOpen;

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleClose();
      }
    },
    [open, handleClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [handler]);

  return (
    <Tooltip
      classes={tooltipClasses}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      title={title}
      {...rest}
    >
      {children}
    </Tooltip>
  );
};
